import { useState, useEffect, useCallback } from 'react';
import * as api from './connector-api';
import type { Connector, ConnectorTemplate, ConnectorTool } from './connector-types';
import { getErrorMessage } from './error-utils';

export function useConnectors() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.listConnectors({ limit: 100 });
      setConnectors(result.connectors);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load connectors'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (data: Parameters<typeof api.createConnector>[0]) => {
    const connector = await api.createConnector(data);
    setConnectors(prev => [...prev, connector]);
    return connector;
  }, []);

  const update = useCallback(async (id: string, data: Parameters<typeof api.updateConnector>[1]) => {
    const connector = await api.updateConnector(id, data);
    setConnectors(prev => prev.map(c => c.id === id ? connector : c));
    return connector;
  }, []);

  const remove = useCallback(async (id: string) => {
    await api.deleteConnector(id);
    setConnectors(prev => prev.filter(c => c.id !== id));
  }, []);

  const test = useCallback(async (id: string) => {
    return api.testConnection(id);
  }, []);

  return { connectors, loading, error, refresh, create, update, remove, test };
}

export function useConnectorTools(connectorId: string | null) {
  const [tools, setTools] = useState<ConnectorTool[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!connectorId) {
      setTools([]);
      return;
    }

    setLoading(true);
    setError(null);
    api.getConnectorTools(connectorId)
      .then(result => setTools(result.tools))
      .catch((err) => {
        console.error('Failed to load connector tools:', err);
        setError(getErrorMessage(err, 'Failed to load tools'));
      })
      .finally(() => setLoading(false));
  }, [connectorId]);

  const discover = useCallback(async () => {
    if (!connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.discoverTools(connectorId);
      setTools(result.tools);
    } catch (err: any) {
      console.error('Failed to discover tools:', err);
      setError(getErrorMessage(err, 'Failed to discover tools'));
    } finally {
      setLoading(false);
    }
  }, [connectorId]);

  return { tools, loading, error, discover };
}

export function useConnectorTemplates() {
  const [templates, setTemplates] = useState<ConnectorTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.listTemplates({
        search: searchTerm || search || undefined,
        limit: 100
      });
      setTemplates(result.templates);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    refresh();
  }, []);

  const instantiate = useCallback(async (
    templateId: string,
    data: Parameters<typeof api.instantiateTemplate>[1]
  ) => {
    return api.instantiateTemplate(templateId, data);
  }, []);

  return { templates, loading, error, search, setSearch, refresh, instantiate };
}

export function useSessionConnectors(sessionId: string | undefined) {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!sessionId) {
      setConnectors([]);
      return;
    }
    try {
      setLoading(true);
      const result = await api.getSessionConnectors(sessionId);
      setConnectors(result.connectors);
    } catch (err) {
      console.error('Failed to load session connectors:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const attach = useCallback(async (connectorId: string) => {
    if (!sessionId) return;
    await api.attachConnectorToSession(sessionId, connectorId);
    await refresh();
  }, [sessionId, refresh]);

  const detach = useCallback(async (connectorId: string) => {
    if (!sessionId) return;
    await api.detachConnectorFromSession(sessionId, connectorId);
    setConnectors(prev => prev.filter(c => c.id !== connectorId));
  }, [sessionId]);

  return { connectors, loading, refresh, attach, detach };
}
